// src/components/AuthScreen.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import animationImage from './animation_image.png';
import officeImage from './office_image.png';
import founderImage from './animation_image.png';
import { Upload } from 'lucide-react';
import suitImage from './suit.png';
import ReplyProcessingOverlay from './ReplyProcessingOverlay';
import DataPreviewOverlay from './DataPreviewOverlay';
import ReceptionHandoffOverlay from './ReceptionHandoffOverlay';
import CampaignSendingOverlay from './CampaignSendingOverlay';
import RecallSuccessOverlay from './RecallSuccessOverlay';
import GuidedSalesDemo from './GuidedSalesDemo'

export default function AuthScreen() {
  // ===== ALL HOOKS AT THE TOP =====
  const [mode, setMode] = useState('welcome');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [practiceName, setPracticeName] = useState('');
  const [loading, setLoading] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const { login, signup, enterDemo } = useAuth();
  const { addToast } = useToast();
  const transitionRef = useRef(false);

  // Hide intro after 3.3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowIntro(false);
    }, 3300);
    return () => clearTimeout(timer);
  }, []);

  // Safety timer for reception handoff
  useEffect(() => {
    if (mode !== 'receptionHandoff') return;
    
    const safetyTimer = setTimeout(() => {
      if (!transitionRef.current) {
        console.log('⏰ Safety timer triggered - forcing transition');
        transitionRef.current = true;
        setMode('recallSuccess');
      }
    }, 30000);
    
    return () => clearTimeout(safetyTimer);
  }, [mode]);

  // Reset transition flag when leaving receptionHandoff
  useEffect(() => {
    if (mode !== 'receptionHandoff') {
      transitionRef.current = false;
    }
  }, [mode]);

  // ===== FUNCTIONS =====
  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        if (!practiceName.trim()) {
          addToast('Practice name is required', 'error');
          setLoading(false);
          return;
        }
        await signup(email, password, practiceName);
      }
    } catch (err) {
      const msgs = {
        'auth/user-not-found': 'No account found with that email.',
        'auth/wrong-password': 'Incorrect password.',
        'auth/email-already-in-use': 'An account with this email already exists.',
        'auth/weak-password': 'Password must be at least 6 characters.',
        'auth/invalid-email': 'Please enter a valid email address.',
      };
      addToast(msgs[err.code] || 'Something went wrong. Please try again.', 'error');
    }
    setLoading(false);
  }

  // ===== CONDITIONAL RETURNS (after all hooks) =====
  
  // Intro Animation
  if (showIntro) {
    return <IntroAnimation onComplete={() => setShowIntro(false)} />;
  }

  // Welcome Screen
    if (mode === 'welcome') {
    return (
      <div style={styles.welcomePage}>
        {/* 🔥 NEW BUTTON – TOP LEFT 🔥 */}
        <button 
  onClick={() => setMode('guidedDemo')} 
  style={guidedDemoBtnStyle}
>
  Guided Sales Demo
</button>

        <div style={styles.globalTopRight}>
          <button onClick={() => setMode('login')} style={styles.signInBtn}>
            Sign in →
          </button>
        </div>

        <div style={styles.leftSide}>
          <div style={styles.photoContainer}>
            <img 
              src={suitImage}
              alt="Bobby Tjia" 
              style={styles.photoImage} 
            />
            <div style={styles.photoGlow} />
          </div>
          <div style={styles.photoCredit}>Bobby Tjia · Founder</div>
        </div>

        <div style={styles.rightSide}>
          <div style={styles.buttonContainer}>
            <button 
              onClick={() => setMode('import')} 
              style={styles.uploadBtn}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.30)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.18)';
              }}
            >
              Upload Basic Patient Info
            </button>

            <button 
              onClick={() => setMode('preview')} 
              style={styles.demoBtn}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 72px rgba(45, 155, 138, 0.7)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 48px rgba(45, 155, 138, 0.5)';
              }}
            >
              <div style={styles.demoHeaderRow}>
                <span style={styles.demoHeaderCell}>NAME</span>
                <span style={styles.demoHeaderCell}>CONTACT</span>
                <span style={styles.demoHeaderCell}>TREATMENT</span>
              </div>
              <div style={styles.demoDivider}></div>
              <div style={styles.demoMainText}>Start Demo with Sample Data</div>
            </button>
          </div>
          <div style={styles.credit}>
            <span style={styles.creditText}>By Bobby Tjia</span>
          </div>
        </div>
      </div>
    );
  }

  // Data Preview
  if (mode === 'preview') {
    return (
      <DataPreviewOverlay 
        onComplete={() => setMode('scriptIntro')}
      />
    );
  }

  // Script Intro
  if (mode === 'scriptIntro') {
    return (
      <ScriptIntroOverlay 
        onComplete={() => setMode('campaignSending')}
      />
    );
  }

  // Campaign Sending
  if (mode === 'campaignSending') {
    return (
      <CampaignSendingOverlay 
        onComplete={() => setMode('replyProcessing')}
        patientCount={30}
      />
    );
  }

  // Reply Processing
  if (mode === 'replyProcessing') {
    return (
      <ReplyProcessingOverlay 
        onComplete={() => setMode('receptionHandoff')}
      />
    );
  }

  // Reception Handoff
  if (mode === 'receptionHandoff') {
    console.log('🔄 Rendering ReceptionHandoffOverlay');
    
    const handleComplete = () => {
      if (transitionRef.current) return;
      console.log('✅ ReceptionHandoff onComplete called!');
      transitionRef.current = true;
      setMode('recallSuccess');
    };
    
    return <ReceptionHandoffOverlay onComplete={handleComplete} />;
  }

  // Recall Success
  if (mode === 'recallSuccess') {
    return (
      <RecallSuccessOverlay 
        onComplete={() => enterDemo()}
      />
    );
  }

  if (mode === 'guidedDemo') {
    return <GuidedSalesDemo onClose={() => setMode('welcome')} />;
  }

  // ===== LOGIN / SIGNUP SCREEN =====
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <button onClick={() => setMode('welcome')} style={styles.backBtn}>
          ← Back
        </button>

        <div style={styles.logo}>
          <div style={styles.logoIcon}>
            <svg width="32" height="32" viewBox="0 0 40 40" fill="none">
              <rect width="40" height="40" rx="10" fill="#1a3a3a"/>
              <path d="M10 15C10 11.5 13 9 16.5 9h7c3.5 0 6.5 2.5 6.5 6 0 4-2.5 6.5-6 7 .5 1.5 2 3 3.5 4.5H12.5c1.5-1.5 3-3 3.5-4.5-3.5-.5-6-3-6-7z" fill="#2D9B8A"/>
            </svg>
          </div>
          <span style={styles.logoText}>RecallReady</span>
        </div>

        <h1 style={styles.heading}>Welcome back</h1>
        <p style={styles.subheading}>Sign in to manage your patient recalls</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          {mode === 'signup' && (
            <div style={styles.field}>
              <label style={styles.label}>Practice name</label>
              <input
                style={styles.input}
                type="text"
                placeholder="e.g. Bright Smiles Dental"
                value={practiceName}
                onChange={e => setPracticeName(e.target.value)}
                required
              />
            </div>
          )}
          <div style={styles.field}>
            <label style={styles.label}>Email address</label>
            <input
              style={styles.input}
              type="email"
              placeholder="you@practice.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input
              style={styles.input}
              type="password"
              placeholder={mode === 'signup' ? 'Minimum 6 characters' : '••••••••'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
          </div>

          <button type="submit" style={styles.primaryBtn} disabled={loading}>
            {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <p style={styles.switchText}>
          {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
          <button 
            onClick={() => setMode(mode === 'login' ? 'signup' : 'login')} 
            style={styles.switchLink}
          >
            {mode === 'login' ? 'Create one' : 'Sign in'}
          </button>
        </p>

        <div style={styles.credit}>
          <span style={styles.creditText}>By Bobby Tjia</span>
        </div>
      </div>
    </div>
  );
}

// ===== SCRIPT INTRO OVERLAY =====
function ScriptIntroOverlay({ onComplete }) {
  const [isVisible, setIsVisible] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [replacedFields, setReplacedFields] = useState([]);
  const [showCompletion, setShowCompletion] = useState(false);
  const [showFinalMessage, setShowFinalMessage] = useState(false);

  const patientData = {
    name: 'Sarah Mitchell',
    practice: 'Bright Smiles Dental',
    visit: '7',
    treatment: 'Routine cleaning'
  };

  const fields = [
    { key: 'name', label: 'Patient Name', value: patientData.name, placeholder: '{firstName}' },
    { key: 'practice', label: 'Practice Name', value: patientData.practice, placeholder: '{practiceName}' },
    { key: 'visit', label: 'Last Visit', value: patientData.visit, placeholder: '{monthsSinceLastVisit}' },
    { key: 'treatment', label: 'Treatment', value: patientData.treatment, placeholder: '{treatmentType}' }
  ];

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const timers = [];
    fields.forEach((field, index) => {
      const delay = 800 + (index * 1200);
      
      const timer = setTimeout(() => {
        setActiveField(field.key);
        
        setTimeout(() => {
          setReplacedFields(prev => [...prev, field.key]);
          setActiveField(null);
          
          if (index === fields.length - 1) {
            setTimeout(() => {
              setShowCompletion(true);
              setTimeout(() => {
                setShowFinalMessage(true);
                setTimeout(() => onComplete(), 2500);
              }, 1800);
            }, 600);
          }
        }, 600);
      }, delay);
      
      timers.push(timer);
    });

    return () => timers.forEach(timer => clearTimeout(timer));
  }, [isVisible, onComplete]);

  const renderTemplate = () => {
    const parts = [
      { text: 'Hi ', isVariable: false },
      { text: '{firstName}', isVariable: true, key: 'name' },
      { text: ',\n\nThis is ', isVariable: false },
      { text: '{practiceName}', isVariable: true, key: 'practice' },
      { text: '.\n\nIt\'s been ', isVariable: false },
      { text: '{monthsSinceLastVisit}', isVariable: true, key: 'visit' },
      { text: ' months.\n\nTime for your ', isVariable: false },
      { text: '{treatmentType}', isVariable: true, key: 'treatment' },
      { text: ' recall!', isVariable: false }
    ];

    return parts.map((part, index) => {
      if (!part.isVariable) {
        return <span key={index} style={scriptStyles.templateText}>{part.text}</span>;
      }
      
      const isReplaced = replacedFields.includes(part.key);
      const isActive = activeField === part.key;
      const value = patientData[part.key];
      
      return (
        <span
          key={index}
          style={{
            ...scriptStyles.variable,
            color: isReplaced ? '#FFFFFF' : '#3ABFA8',
            background: isReplaced ? 'rgba(45, 155, 138, 0.25)' : 'rgba(45, 155, 138, 0.15)',
            border: isActive ? '2px solid #3ABFA8' : '2px solid transparent',
            boxShadow: isActive ? '0 0 20px rgba(45, 155, 138, 0.3)' : 'none',
            transform: isActive ? 'scale(1.05)' : 'scale(1)',
            transition: 'all 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
            padding: isReplaced ? '2px 8px' : '2px 6px',
            borderRadius: '4px',
            display: 'inline-block',
          }}
        >
          {isReplaced ? value : part.text}
          {isReplaced && (
            <span style={{
              display: 'inline-block',
              marginLeft: '4px',
              color: '#3ABFA8',
              fontSize: '12px',
              animation: 'fadeIn 0.3s ease-out',
            }}>✓</span>
          )}
        </span>
      );
    });
  };

  return (
    <div style={scriptStyles.overlay}>
      <div style={scriptStyles.glowBg} />
      <div 
        style={{
          ...scriptStyles.content,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        }}
      >
        <div style={scriptStyles.header}>
          <span style={scriptStyles.headerText}>Automated Personalization</span>
          <span style={scriptStyles.headerBadge}>Live Demo</span>
        </div>

        <div style={scriptStyles.patientCard}>
          <div style={scriptStyles.patientCardHeader}>Patient Data</div>
          <div style={scriptStyles.patientCardGrid}>
            {fields.map((field) => (
              <div 
                key={field.key}
                style={{
                  ...scriptStyles.patientCardRow,
                  background: activeField === field.key ? 'rgba(45, 155, 138, 0.15)' : 'transparent',
                  borderLeft: activeField === field.key ? '3px solid #3ABFA8' : '3px solid transparent',
                  transition: 'all 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
                  paddingLeft: activeField === field.key ? '12px' : '8px',
                }}
              >
                <span style={scriptStyles.patientLabel}>{field.label}</span>
                <span style={{
                  ...scriptStyles.patientValue,
                  color: replacedFields.includes(field.key) ? '#3ABFA8' : 'rgba(255,255,255,0.9)',
                }}>
                  {field.value}
                  {replacedFields.includes(field.key) && (
                    <span style={scriptStyles.checkmark}> ✓</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div style={scriptStyles.templateContainer}>
          <div style={scriptStyles.templateHeader}>
            <span style={scriptStyles.templateTitle}>Your Script Template</span>
            <span style={scriptStyles.templateStatus}>
              {replacedFields.length === 0 && 'Waiting for data...'}
              {replacedFields.length > 0 && replacedFields.length < 4 && 
                `${replacedFields.length}/4 variables inserted`}
              {replacedFields.length === 4 && '✓ All variables inserted'}
            </span>
          </div>
          
          <div style={scriptStyles.templateContent}>
            {renderTemplate()}
          </div>

          <div style={scriptStyles.progressContainer}>
            <div style={{
              ...scriptStyles.progressBar,
              width: `${(replacedFields.length / 4) * 100}%`,
              background: 'linear-gradient(90deg, #2D9B8A, #3ABFA8)',
            }} />
          </div>
        </div>

        {showCompletion && (
          <div style={scriptStyles.completionBadge}>
            <span style={scriptStyles.completionIcon}>✓</span>
            <span style={scriptStyles.completionText}>4/4 variables inserted</span>
            <span style={scriptStyles.completionSubtext}>Message Ready</span>
          </div>
        )}

        <div style={{
          ...scriptStyles.finalPanel,
          transform: showFinalMessage ? 'translateX(0)' : 'translateX(120%)',
          opacity: showFinalMessage ? 1 : 0,
        }}>
          <div style={scriptStyles.finalPanelHeader}>
            <span style={scriptStyles.finalPanelTitle}>✓ Final Message</span>
            <span style={scriptStyles.finalPanelBadge}>Ready to Send</span>
          </div>
          <div style={scriptStyles.finalPanelContent}>
            <div style={scriptStyles.finalMessageText}>
              Hi Sarah,
              <br /><br />
              This is Bright Smiles Dental.
              <br /><br />
              It's been 7 months.
              <br /><br />
              Time for your Routine cleaning recall!
            </div>
          </div>
          <div style={scriptStyles.finalPanelFooter}>
            Personalized for Sarah Mitchell
          </div>
        </div>

        <div style={scriptStyles.footer}>
          <span style={scriptStyles.footerText}>
            {!showFinalMessage && 'Automatically personalizing your message...'}
            {showFinalMessage && '✓ Message ready for delivery'}
          </span>
        </div>
      </div>
    </div>
  );
}

const scriptStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: '#070E16',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Inter', -apple-system, sans-serif",
    padding: 24,
    overflow: 'hidden',
  },
  glowBg: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: 900,
    height: '70%',
    maxHeight: 700,
    background: 'radial-gradient(circle, rgba(45, 155, 138, 0.12) 0%, rgba(45, 155, 138, 0.04) 40%, rgba(45, 155, 138, 0) 70%)',
    filter: 'blur(60px)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  content: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.8s cubic-bezier(0.22, 1, 0.36, 1)',
    maxWidth: 800,
    width: '100%',
    gap: 16,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    marginBottom: 4,
  },
  headerText: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 16,
    fontWeight: 600,
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },
  headerBadge: {
    background: 'rgba(45, 155, 138, 0.2)',
    color: '#3ABFA8',
    padding: '2px 12px',
    borderRadius: '12px',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.5px',
  },
  patientCard: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    padding: '16px 20px',
    width: '100%',
    maxWidth: 600,
  },
  patientCardHeader: {
    fontSize: 11,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.3)',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: 10,
  },
  patientCardGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '6px 0',
  },
  patientCardRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 8px',
    borderRadius: 4,
    transition: 'all 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
  },
  patientLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: 'rgba(255,255,255,0.4)',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  patientValue: {
    fontSize: 13,
    fontWeight: 600,
    transition: 'color 0.5s ease',
  },
  checkmark: {
    color: '#3ABFA8',
    fontSize: 14,
    marginLeft: 4,
  },
  templateContainer: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 16,
    padding: '24px 28px',
    width: '100%',
    maxWidth: 600,
    position: 'relative',
  },
  templateHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  templateTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.4)',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  templateStatus: {
    fontSize: 12,
    fontWeight: 500,
    color: 'rgba(255,255,255,0.3)',
    transition: 'all 0.5s ease',
  },
  templateContent: {
    fontSize: 15,
    lineHeight: 1.8,
    color: 'rgba(255,255,255,0.8)',
    fontFamily: "'JetBrains Mono', monospace",
    minHeight: 120,
    padding: '4px 0',
    whiteSpace: 'pre-wrap',
  },
  templateText: {
    fontSize: 15,
    lineHeight: 1.8,
    color: 'rgba(255,255,255,0.7)',
  },
  variable: {
    fontWeight: 600,
    transition: 'all 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
    display: 'inline-block',
  },
  progressContainer: {
    width: '100%',
    height: 2,
    background: 'rgba(255,255,255,0.06)',
    borderRadius: 2,
    overflow: 'hidden',
    marginTop: 16,
  },
  progressBar: {
    height: '100%',
    borderRadius: 2,
    transition: 'width 0.8s cubic-bezier(0.22, 1, 0.36, 1)',
  },
  completionBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: 'rgba(45, 155, 138, 0.15)',
    border: '1px solid rgba(45, 155, 138, 0.3)',
    borderRadius: 30,
    padding: '8px 20px',
    animation: 'fadeIn 0.6s ease-out',
  },
  completionIcon: {
    color: '#3ABFA8',
    fontSize: 18,
    fontWeight: 700,
  },
  completionText: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 14,
    fontWeight: 600,
  },
  completionSubtext: {
    color: '#3ABFA8',
    fontSize: 13,
    fontWeight: 500,
    paddingLeft: 8,
    borderLeft: '1px solid rgba(45, 155, 138, 0.3)',
  },
  finalPanel: {
    position: 'fixed',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 340,
    background: 'rgba(255,255,255,0.05)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRight: 'none',
    borderRadius: '16px 0 0 16px',
    padding: '24px 28px',
    transition: 'all 0.8s cubic-bezier(0.22, 1, 0.36, 1)',
    boxShadow: '-8px 0 40px rgba(0,0,0,0.4)',
  },
  finalPanelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  finalPanelTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.8)',
  },
  finalPanelBadge: {
    fontSize: 10,
    fontWeight: 600,
    color: '#3ABFA8',
    background: 'rgba(45, 155, 138, 0.2)',
    padding: '2px 12px',
    borderRadius: 12,
  },
  finalPanelContent: {
    background: 'rgba(255,255,255,0.03)',
    borderRadius: 8,
    padding: '16px 20px',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  finalMessageText: {
    fontSize: 14,
    lineHeight: 1.7,
    color: 'rgba(255,255,255,0.9)',
    fontFamily: "'JetBrains Mono', monospace",
  },
  finalPanelFooter: {
    marginTop: 12,
    fontSize: 11,
    color: 'rgba(255,255,255,0.3)',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  footer: {
    marginTop: 8,
  },
  footerText: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.25)',
    letterSpacing: '0.3px',
  },
};

// ===== INTRO ANIMATION =====
function IntroAnimation({ onComplete }) {
  const [isVisible, setIsVisible] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFadeOut(true);
      setTimeout(() => {
        setIsVisible(false);
        if (onComplete) onComplete();
      }, 800);
    }, 5500);
    return () => clearTimeout(timer);
  }, [onComplete]);

  if (!isVisible) return null;

  return (
    <div style={{
      ...introStyles.container,
      opacity: fadeOut ? 0 : 1,
      transition: 'opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1)',
    }}>
      <div style={introStyles.background} />
      <div style={introStyles.content}>
        <div style={introStyles.headlineWrapper}>
          <h1 style={introStyles.headline}>
            Bring overdue patients
            <br />
            <span style={introStyles.headlineHighlight}>back automatically.</span>
          </h1>
          <p style={introStyles.subtext}>
            We handle your patient recalls from start to finish.
          </p>
        </div>
        <div style={introStyles.founderWrapper}>
          <img
            src={animationImage}
            alt="Bobby Tjia - Founder"
            style={introStyles.founder}
          />
          <div style={introStyles.founderCredit}>Bobby Tjia · Founder</div>
        </div>
        <div style={introStyles.bar}>
          <div style={introStyles.progress} />
        </div>
      </div>
    </div>
  );
}

// ===== STYLES =====
const introStyles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#070E16',
    fontFamily: "'Inter', -apple-system, sans-serif",
    zIndex: 9999,
    overflow: 'hidden',
  },
  background: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(ellipse at 50% 90%, 
        #091320 0%, 
        #070E16 45%, 
        #060C12 75%,
        #070E16 100%
      )
    `,
    zIndex: 0,
    boxShadow: 'inset 0 0 120px rgba(6, 12, 18, 0.4)',
  },
  content: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 20px',
    maxWidth: 700,
    width: '100%',
    gap: 16,
  },
  headlineWrapper: {
    textAlign: 'center',
    animation: 'slideUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.2s both',
  },
  headline: {
    fontSize: 40,
    fontWeight: 700,
    color: '#FFFFFF',
    margin: 0,
    lineHeight: 1.2,
    letterSpacing: '-1.5px',
    fontFamily: "'Inter', sans-serif",
  },
  headlineHighlight: {
    color: '#2D9B8A',
  },
  subtext: {
    fontSize: 17,
    color: 'rgba(255,255,255,0.5)',
    marginTop: 10,
    letterSpacing: '0.3px',
    fontFamily: "'Inter', sans-serif",
    animation: 'fadeIn 0.8s ease-out 0.6s both',
  },
  founderWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    animation: 'fadeIn 1s ease-out 0.9s both',
  },
  founder: {
    height: '40vh',
    maxHeight: 340,
    width: 'auto',
    objectFit: 'contain',
    opacity: 0.7,
    filter: 'brightness(0.8) contrast(1.0) saturate(0.9)',
  },
  founderCredit: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.25)',
    marginTop: 6,
    letterSpacing: '1px',
    fontFamily: "'Inter', sans-serif",
    fontWeight: 300,
  },
  bar: {
    width: 200,
    height: 3,
    background: 'rgba(255,255,255,0.1)',
    borderRadius: 2,
    marginTop: 4,
    overflow: 'hidden',
    animation: 'fadeIn 0.7s ease-out 1.8s both',
  },
  progress: {
    height: '100%',
    width: '0%',
    background: 'linear-gradient(90deg, #2D9B8A, #3ABFA8)',
    borderRadius: 2,
    animation: 'load 3.5s ease-in forwards',
  },
};

const styles = {
  welcomePage: {
    minHeight: '100vh',
    background: '#070E16',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Inter', -apple-system, sans-serif",
    position: 'relative',
    overflow: 'hidden',
    padding: '0 40px',
    gap: '40px',
  },
  globalTopRight: {
    position: 'absolute',
    top: 24,
    right: 32,
    zIndex: 10,
  },
  leftSide: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: '500px',
    zIndex: 1,
  },
  photoContainer: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  photoImage: {
    width: '100%',
    maxWidth: '400px',
    height: 'auto',
    borderRadius: '20px',
    objectFit: 'cover',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  photoGlow: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '120%',
    height: '120%',
    background: 'radial-gradient(circle, rgba(45, 155, 138, 0.15) 0%, rgba(45, 155, 138, 0) 70%)',
    filter: 'blur(40px)',
    pointerEvents: 'none',
    zIndex: -1,
  },
  photoCredit: {
    marginTop: '16px',
    fontSize: '14px',
    color: 'rgba(255,255,255,0.3)',
    letterSpacing: '1px',
    fontWeight: 400,
    fontFamily: "'Inter', sans-serif",
  },
  rightSide: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: '500px',
    position: 'relative',
    zIndex: 1,
    paddingTop: '60px',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '100%',
    maxWidth: '480px',
  },
  signInBtn: {
    padding: '10px 22px',
    background: 'linear-gradient(135deg, #2D9B8A 0%, #238A7A 100%)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '30px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
    boxShadow: '0 0 24px rgba(45, 155, 138, 0.35)',
    letterSpacing: '0.3px',
    transition: 'all 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  },
  uploadBtn: {
    width: '100%',
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255, 255, 255, 0.06)',
    color: '#FFFFFF',
    border: '1.5px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '16px',
    fontSize: '20px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
    letterSpacing: '-0.2px',
    backdropFilter: 'blur(4px)',
    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  },
  demoBtn: {
    width: '100%',
    padding: '14px 24px 18px 24px',
    background: 'linear-gradient(135deg, #2D9B8A 0%, #238A7A 100%)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '24px',
    cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
    boxShadow: '0 4px 48px rgba(45, 155, 138, 0.5)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
    transition: 'all 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  },
  demoHeaderRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    width: '100%',
    maxWidth: '480px',
    gap: '0px',
    marginBottom: '2px',
  },
  demoHeaderCell: {
    fontSize: '13px',
    fontWeight: 700,
    color: 'rgba(255,255,255,0.85)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontFamily: "'JetBrains Mono', monospace",
    padding: '2px 0',
    borderBottom: '1px solid rgba(255,255,255,0.25)',
    textAlign: 'center',
    borderRight: '1px solid rgba(255,255,255,0.15)',
  },
  demoDivider: {
    width: '100%',
    maxWidth: '480px',
    height: '1px',
    background: 'rgba(255,255,255,0.15)',
    margin: '2px 0 4px 0',
  },
  demoMainText: {
    fontSize: '28px',
    fontWeight: 700,
    letterSpacing: '-0.3px',
  },
  credit: {
    marginTop: '32px',
  },
  creditText: {
    fontSize: '12px',
    fontWeight: 500,
    color: 'rgba(255,255,255,0.15)',
    letterSpacing: '0.5px',
    fontFamily: "'Inter', sans-serif",
  },
  page: {
    minHeight: '100vh',
    background: '#070E16',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    fontFamily: "'Inter', -apple-system, sans-serif",
  },
  card: {
    background: 'rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: '20px',
    padding: '40px 36px 28px',
    width: '100%',
    maxWidth: 400,
    boxShadow: '0 8px 48px rgba(0, 0, 0, 0.5)',
    position: 'relative',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.4)',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    fontFamily: "'Inter', sans-serif",
    padding: 0,
    marginBottom: 20,
    transition: 'color 0.2s',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 24,
  },
  logoIcon: {
    flexShrink: 0,
  },
  logoText: {
    fontWeight: 700,
    fontSize: 18,
    color: '#FFFFFF',
    letterSpacing: '-0.5px',
  },
  heading: {
    fontSize: 24,
    fontWeight: 700,
    color: '#FFFFFF',
    margin: '0 0 4px 0',
    letterSpacing: '-0.5px',
  },
  subheading: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.5)',
    margin: '0 0 24px 0',
    lineHeight: 1.5,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.6)',
    letterSpacing: '0.3px',
  },
  input: {
    padding: '12px 16px',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    fontSize: 14,
    color: '#FFFFFF',
    background: 'rgba(255,255,255,0.06)',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    fontFamily: "'Inter', sans-serif",
  },
  primaryBtn: {
    marginTop: 4,
    padding: '13px 0',
    background: 'linear-gradient(135deg, #2D9B8A 0%, #238A7A 100%)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '10px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'transform 0.1s, box-shadow 0.2s',
    fontFamily: "'Inter', sans-serif",
    boxShadow: '0 4px 16px rgba(45, 155, 138, 0.25)',
  },
  switchText: {
    textAlign: 'center',
    fontSize: 13,
    color: 'rgba(255,255,255,0.4)',
    marginTop: 16,
    marginBottom: 0,
  },
  switchLink: {
    background: 'none',
    border: 'none',
    color: '#2D9B8A',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 13,
    padding: 0,
    fontFamily: "'Inter', sans-serif",
  },
};
const guidedDemoBtnStyle = {
  position: 'absolute',
  top: 24,
  left: 32,
  zIndex: 10,
  padding: '8px 18px',
  background: '#2D9B8A',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: 30,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: "'Inter', sans-serif",
  boxShadow: '0 2px 12px rgba(45, 155, 138, 0.25)',
  transition: 'all 0.2s ease',
};

// ===== KEYFRAMES =====
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes slideUp {
    0% { transform: translateY(30px); opacity: 0; }
    100% { transform: translateY(0); opacity: 1; }
  }
  @keyframes fadeIn {
    0% { opacity: 0; }
    100% { opacity: 1; }
  }
  @keyframes load {
    0% { width: 0%; }
    100% { width: 100%; }
  }
`;
document.head.appendChild(styleSheet);

const scriptStyleSheet = document.createElement("style");
scriptStyleSheet.textContent = `
  @keyframes fadeIn {
    0% { opacity: 0; transform: translateY(10px); }
    100% { opacity: 1; transform: translateY(0); }
  }
`;
document.head.appendChild(scriptStyleSheet);